/**
 * 答えられなかった質問の一覧（issue #109・docs/01 11-9）。
 *
 * 「現場が何に迷っているか」がそのまま出てきます。ここから答えを書いて承認すると、
 * 次から AI先輩が答えられるようになります（11-6 の学習のループ）。
 *
 * **答えない領域（労務・健康など）の質問は、この一覧には出ません。**
 * 誰が何を聞いたかを管理画面に出さないためで、件数だけが「利用状況」に出ます（11-9）。
 * 見分けは「答えない領域」の画面にある言葉で行うので、言葉に当たらない相談はここに出ます（issue #261）。
 * そのため**どなたが・どのトークで聞いたかは出しません**（issue #375）。答えを書くのにお名前は要らず、
 * 見分けをすり抜けた相談があっても、個人と結びつかないようにするためです。
 * 受け持ちが自拠点の方には、自拠点の方の質問だけが出ます（issue #253・docs/01 15-1）。
 *
 * 一度に読むのは50件ずつで、続きがあれば「さらに表示する」で読み足します（issue #238）。
 * 答え（承認待ち・使われているもの）がもうある質問は、その答えの画面へつなぎます（issue #264）。
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/context.js';
import { usePagedList } from '../api/paged-list.js';
import type { AiUnansweredItem, AiUnansweredPage as AiUnansweredPageData } from '../api/types.js';
import { LoadMoreFooter } from '../components/LoadMoreFooter.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { formatTableDateTime } from '../format/datetime.js';

const PAGE_SIZE = 50;

export function AiUnansweredPage(): ReactNode {
  const api = useApi();
  const list = usePagedList<AiUnansweredItem>(
    'unanswered',
    async (cursor) => {
      const loaded = await api.get<AiUnansweredPageData>('/admin/ai/unanswered', {
        query: { limit: PAGE_SIZE, ...(cursor !== null ? { before: cursor } : {}) },
      });
      return { items: loaded.items, next: loaded.nextCursor };
    },
    (item) => item.id,
  );
  const items = list.items;

  return (
    <article>
      <PageHeader
        title="答えられなかった質問"
        description="AI先輩が答えられなかった質問です。どなたの質問かは出していません。答えを書いて承認すると、次から答えられるようになります。"
      />
      <p className="admin-note">
        給与・労務・健康など、答えない領域の言葉が入った質問はここには出ません（件数だけを「利用状況」でご覧いただけます）。言葉は「答えない領域」の画面でご確認いただけます。
      </p>
      <p className="admin-note">受け持ちが自拠点の方には、自拠点の方の質問だけが出ます。</p>

      {list.error !== null ? <ErrorBlock failure={list.error} onRetry={list.reload} /> : null}
      {list.error === null && items === null ? (
        <LoadingBlock label="質問を読み込んでいます…" />
      ) : null}
      {items && items.length === 0 ? (
        <EmptyBlock label="いまは答えられなかった質問がありません。" />
      ) : null}

      {items && items.length > 0 ? (
        <DataTable label="答えられなかった質問の一覧">
          <thead>
            <tr>
              <th scope="col">質問</th>
              <th scope="col">聞かれた日時</th>
              <th scope="col">いまの状態</th>
              <th scope="col">
                <span className="admin-visually-hidden">開く</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td className="ui-cell-wrap">{item.question}</td>
                <td className="ui-cell-date">{formatTableDateTime(item.created_at)}</td>
                <td>{answerStateLabel(item)}</td>
                <td>
                  {item.draft_id ? (
                    // 答えがもうある質問は、その答えを開いて直す（新しく書くと2つになる・issue #264）
                    <Link
                      className="ui-button ui-button-secondary ui-button-compact"
                      to={`/ai/knowledge/${item.draft_id}`}
                    >
                      答えを開く
                    </Link>
                  ) : (
                    <Link
                      className="ui-button ui-button-secondary ui-button-compact"
                      to={`/ai/questions/${item.id}`}
                    >
                      答えを書く
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}

      <LoadMoreFooter list={list} />
    </article>
  );
}

/**
 * 答えの状態を言葉にする（#286 L-11）。
 * いま生きている答え（承認待ち・使われている）があるときは「答えがあります」。
 * 取り下げた答えしか無いときは、そう言う（「下書きがあります」と言うと、開く先が無いのに迷わせる）。
 */
function answerStateLabel(item: AiUnansweredItem): string {
  if (item.draft_id) return '答えがあります';
  if (item.has_draft) return '取り下げた答えだけがあります';
  return 'まだ答えがありません';
}
